import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import { Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import { ConfirmDeleteModal } from 'src/components/modals/ConfirmDeleteModal';
import { showErrorToast, showSuccessToast } from 'src/components/Toast';
import { formatDate } from 'src/utils/misc';
import {
  DELETE_CONTROL_FRAMEWORK_VERSION,
  DeleteControlFrameworkVersionMutation,
  DeleteControlFrameworkVersionMutationVariables,
} from 'src/graphql/contentImport';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10;
  background: rgba(0, 0, 0, 0.16);
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  z-index: 11;
  width: min(460px, 100vw);
  height: 100vh;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 0 37px ${({ theme }) => theme.colors.shadow};
  padding: 26px;
  overflow-y: auto;

  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  .close {
    border: 0;
    background: transparent;
    color: ${({ theme }) => theme.colors.cflowerBlue};
    cursor: pointer;
    font-size: 22px;
    line-height: 1;
  }

  .version-table {
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    border-radius: 6px;
    overflow: hidden;
  }

  .row {
    display: grid;
    grid-template-columns: 96px 1fr 110px;
    gap: 12px;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
  }

  .row:last-child {
    border-bottom: 0;
  }

  .row--head {
    background: ${({ theme }) => theme.colors.cultured};
  }

  .current {
    border: 1px solid ${({ theme }) => theme.colors.gp1};
    color: ${({ theme }) => theme.colors.gp1};
    border-radius: 3px;
    padding: 3px 8px;
    font-size: 11px;
    font-weight: 600;
    justify-self: start;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
  }
`;

export type ControlFrameworkSummary = {
  id: string;
  name: string;
  shortname?: string | null;
  author?: string | null;
  organisation?: {
    id: string;
    name: string;
    logoUrl?: string | null;
  } | null;
  description?: string | null;
  featureKey?: string | null;
  version?: string | null;
  publishDate?: string | null;
  versionNumber?: number | null;
  isCurrentVersion?: boolean | null;
  versionCreatedBy?: string | null;
  controlCount?: number | null;
  frameworkControls?: { id: string }[];
  meta?: {
    createdAt?: string | null;
    updatedAt?: string | null;
  } | null;
  versions?: ControlFrameworkVersion[];
};

export type ControlFrameworkVersion = Omit<ControlFrameworkSummary, 'versions'>;

interface ControlFrameworkVersionHistoryProps {
  framework: ControlFrameworkSummary;
  onClose: () => void;
  onChanged: (currentFrameworkId?: string | null) => Promise<void> | void;
}

export const ControlFrameworkVersionHistory: React.FC<ControlFrameworkVersionHistoryProps> = ({
  framework,
  onClose,
  onChanged,
}) => {
  const [versionToDelete, setVersionToDelete] = useState<ControlFrameworkVersion | null>(null);
  const [deleteVersion, { loading }] = useMutation<
    DeleteControlFrameworkVersionMutation,
    DeleteControlFrameworkVersionMutationVariables
  >(DELETE_CONTROL_FRAMEWORK_VERSION);
  const versions = [...(framework.versions || [])].sort(
    (a, b) => (b.versionNumber || 0) - (a.versionNumber || 0),
  );

  const handleDeleteVersion = async () => {
    if (!versionToDelete) return;

    try {
      const result = await deleteVersion({ variables: { frameworkId: versionToDelete.id } });
      setVersionToDelete(null);
      showSuccessToast({ title: 'Control framework version deleted' });
      await onChanged(result.data?.deleteControlFrameworkVersion.frameworkId);
    } catch (error: any) {
      setVersionToDelete(null);
      showErrorToast(error?.message || 'Could not delete control framework version.');
    }
  };

  return (
    <>
      <Backdrop onClick={onClose} />
      <Panel className="styled-scroll" data-cy="control-framework-version-history">
        <div className="head">
          <div>
            <Poppins px={14} color="cflowerBlue">
              Version History
            </Poppins>
            <Spacer $px={12} />
            <Poppins px={18} weight={600}>
              {framework.shortname || framework.name}
            </Poppins>
          </div>
          <button className="close" type="button" onClick={onClose} aria-label="Close version history">
            x
          </button>
        </div>
        <Spacer $px={26} />
        <div className="version-table">
          <div className="row row--head">
            <Poppins px={12} weight={600}>
              Version
            </Poppins>
            <Poppins px={12} weight={600}>
              Published by
            </Poppins>
            <Poppins px={12} weight={600}>
              Date
            </Poppins>
          </div>
          {versions.map((version) => {
            const controlsCount = version.controlCount ?? version.frameworkControls?.length ?? 0;

            return (
              <div className="row" key={version.id}>
                <div>
                  <Poppins px={12} weight={600}>
                    Version {version.versionNumber || 1}.0
                  </Poppins>
                  <Spacer $px={5} />
                  <Poppins px={12} color="cflowerBlue">
                    {controlsCount} {controlsCount === 1 ? 'control' : 'controls'}
                  </Poppins>
                </div>
                <div>
                  <Poppins px={12}>{version.versionCreatedBy || 'N/A'}</Poppins>
                  {version.isCurrentVersion && (
                    <>
                      <Spacer $px={7} />
                      <span className="current">Current</span>
                    </>
                  )}
                </div>
                <div>
                  <Poppins px={12}>
                    {formatDate(version.meta?.createdAt, { formatType: 'datetime', emptyValue: '-' })}
                  </Poppins>
                  <Spacer $px={8} />
                  <div className="actions">
                    <Button
                      secondary
                      onClick={() => setVersionToDelete(version)}
                      disabled={loading || versions.length <= 1}
                      $constWidth={82}
                    >
                      DELETE
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
      {versionToDelete && (
        <ConfirmDeleteModal
          title={`Delete version ${versionToDelete.versionNumber || 1}.0?`}
          confirmText="DELETE"
          onClose={() => setVersionToDelete(null)}
          onConfirm={handleDeleteVersion}
        >
          <Poppins px={14} color="cflowerBlue">
            This removes the selected control framework version from version history. If it is current, the latest
            remaining version will become current.
          </Poppins>
        </ConfirmDeleteModal>
      )}
    </>
  );
};
